import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useT, usePageTitle, Trans } from "@/i18n/LanguageContext";

export default function Contact() {
  const t = useT();
  usePageTitle(t("meta.title.contact"));
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [kind, setKind] = useState("showroom");
  const [submitting, setSubmitting] = useState(false);
  // Anti-spam: honeypot value + when the form was first shown
  const [honeypot, setHoneypot] = useState("");
  const mountedAt = useRef(Date.now());

  // Mirrors the backend rules: name + phone + message are required, email is optional.
  const validateForm = () => {
    if (form.name.trim().length < 2) {
      toast.error(t("prod.inquire.nameRequired")); return false;
    }
    if (!/^\+?[\d\s\-().]{7,20}$/.test(form.phone)) {
      toast.error(t("prod.inquire.phoneInvalid")); return false;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t("prod.inquire.emailInvalid")); return false;
    }
    if (form.message.trim().length < 5) {
      toast.error(t("prod.inquire.messageRequired")); return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim(),
        message: form.message.trim(),
        kind,
        website: honeypot,
        form_time: Date.now() - mountedAt.current,
      });
      toast.success(t("contact.form.success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || t("contact.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12" data-testid="contact-hero">
        <p className="overline reveal">{t("contact.overline")}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] mt-6 reveal reveal-delay-1 max-w-5xl">
          <Trans tKey="contact.title" />
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-y-10 md:gap-10 pb-20" data-testid="contact-grid">
        <aside className="col-span-12 md:col-span-5 space-y-12">
          <Block label={t("contact.showroomLabel")}>
            Pod Telecentru<br />
            Strada Mioriţa 2/1<br />
            Chișinău, Moldova
          </Block>
          <Block label={t("contact.hoursLabel")}>
            <Trans tKey="contact.hoursBody" />
            <br /><span className="text-melilux-cream/55">{t("contact.closed")}</span>
          </Block>
          <Block label={t("contact.lineLabel")}>
            <a href="tel:+37369090408" className="link-underline text-melilux-gold">+373 690 90 408</a><br />
            <a href="mailto:bazatin-prest@mail.ru" className="text-melilux-cream/85 link-underline">bazatin-prest@mail.ru</a>
          </Block>
          <Block label={t("contact.onlineLabel")}>
            <a href="https://melilux.md" target="_blank" rel="noreferrer" className="text-melilux-gold link-underline">melilux.md</a>
          </Block>
          <div className="border-t border-white/10 pt-8">
            <p className="overline">{t("contact.pressLabel")}</p>
            <p className="text-sm text-melilux-cream/70 mt-3 leading-relaxed max-w-sm">
              {t("contact.pressBody")}{" "}
              <a href="mailto:bazatin-prest@mail.ru" className="text-melilux-gold link-underline">bazatin-prest@mail.ru</a>.
            </p>
          </div>
        </aside>

        <div className="col-span-12 md:col-span-7" data-testid="contact-form">
          <div className="border border-white/10 p-8 md:p-12 bg-melilux-surface">
            <p className="overline">{t("contact.form.overline")}</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
              {t("contact.form.title")}
            </h2>

            <div className="mt-8 flex gap-2 flex-wrap">
              {[
                { id: "showroom", label: t("contact.form.kind.showroom") },
                { id: "general", label: t("contact.form.kind.general") },
                { id: "product", label: t("contact.form.kind.product") },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setKind(opt.id)}
                  data-testid={`kind-${opt.id}`}
                  className={`text-[0.7rem] uppercase tracking-[0.28em] px-4 py-2 border transition-colors ${
                    kind === opt.id
                      ? "border-melilux-gold text-melilux-gold"
                      : "border-white/15 text-melilux-cream/65 hover:border-melilux-cream/40 hover:text-melilux-cream"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-10 space-y-6">
              {/* Honeypot — offscreen, humans never see or fill it */}
              <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              <input type="text" placeholder={t("contact.form.name") + " *"} className="editorial-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" maxLength={100} required />
              <input type="tel" placeholder={t("contact.form.phone") + " *"} className="editorial-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" maxLength={20} required />
              <input type="email" placeholder={t("contact.form.email")} className="editorial-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" maxLength={150} />
              <textarea placeholder={t("contact.form.message") + " *"} className="editorial-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" maxLength={2000} required />
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="bg-melilux-gold text-melilux-bg px-10 py-4 text-[0.72rem] uppercase tracking-[0.3em] hover:opacity-80 transition-opacity disabled:opacity-60"              >
                {submitting ? t("contact.form.sending") : t("contact.form.submit")}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-32" data-testid="contact-map">
        <p className="overline mb-5">{t("contact.mapLabel")}</p>
        <div className="border border-white/10 aspect-[4/3] md:aspect-[16/8] overflow-hidden">
          <iframe
            title="Melilux Chișinău studio"
            src="https://www.google.com/maps?q=Melilux%2C%20Strada%20Miori%C5%A3a%202%2F1%2C%20Chi%C8%99in%C4%83u%2C%20Moldova&output=embed"
            className="w-full h-full"
            style={{ border: 0, filter: "grayscale(0.4) contrast(0.9) brightness(0.85)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </section>
    </>
  );
}

function Block({ label, children }) {
  return (
    <div>
      <p className="overline">{label}</p>
      <p className="mt-4 text-melilux-cream/85 leading-relaxed">{children}</p>
    </div>
  );
}
